import { ActionIcon, Box, Popover, Stack, Tooltip } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';

import RequestInformationForm from '../../mailboxes/SupportForm';
import WatchlistIcon from '@/assets/icons/content/eye-add.svg';
import Button from '@/components/content/button/Button';

import { useToast } from '@/hooks/useToast';
import { useQueryActions } from '@/hooks/useQueryActions';
import { unifiedApiKeys } from '@/services/keys';
import { INCIDENTS_ENDPOINTS, incidentsKeys } from '../services/keys';
import { SUPPORT_TYPES } from '../../mailboxes/types';
import { today } from '@/components/content/date/datepicker/presets';
import {
  compactIncidentActionButtonClassNames,
  type IncidentActionButtonVariant,
} from './incidentActionButton';

import type { FetchError } from '@/types/api';

interface RequestAddToWatchlistProps {
  issueId: number;
  disabled?: boolean;
  loading?: boolean;
  asButton?: boolean;
  size?: number;
  buttonVariant?: IncidentActionButtonVariant;
}

const RequestAddToWatchlist = ({
  issueId,
  loading,
  disabled,
  asButton,
  size = 20,
  buttonVariant = 'default',
}: RequestAddToWatchlistProps) => {
  const [opened, { open, close, toggle }] = useDisclosure(false);
  const { invalidateQueries } = useQueryActions();
  const { createToast } = useToast();

  const onFormSuccess = () => {
    invalidateQueries([
      unifiedApiKeys.ticket(INCIDENTS_ENDPOINTS.incidents, issueId),
      incidentsKeys.incidentsTable(),
    ]);

    createToast('Watchlist request sent succesfully', 'success');
    close();
  };

  const onError = (error: FetchError) => {
    createToast(error.message, 'error');
    close();
  };

  return (
    <Popover
      width={300}
      opened={opened}
      onChange={toggle}
      position="bottom"
      withinPortal={false}
      withArrow
      shadow="md"
      trapFocus
    >
      <Popover.Target>
        {asButton ? (
          <Box w={{ base: '100%', s: 'auto' }}>
            <Button
              onClick={open}
              size="sm"
              variant={buttonVariant === 'compactOutline' ? 'outline' : undefined}
              color={buttonVariant === 'compactOutline' ? '#454bd1' : undefined}
              classNames={
                buttonVariant === 'compactOutline'
                  ? compactIncidentActionButtonClassNames
                  : undefined
              }
              rightSection={
                <WatchlistIcon
                  width={buttonVariant === 'compactOutline' ? 16 : 19}
                  height={buttonVariant === 'compactOutline' ? 16 : 19}
                />
              }
              disabled={disabled}
              loading={loading}
            >
              Add to Watchlist
            </Button>
          </Box>
        ) : (
          <Stack align="center">
            <Tooltip label="Add to watchlist">
              <ActionIcon
                size={size}
                variant="subtle"
                aria-label={`Add ${issueId} to watchlist`}
                disabled={disabled}
                loading={loading}
                loaderProps={{ size: size - 4 }}
                onClick={open}
              >
                <WatchlistIcon />
              </ActionIcon>
            </Tooltip>
          </Stack>
        )}
      </Popover.Target>

      <Popover.Dropdown>
        <RequestInformationForm
          disabledFields={{ subject: true }}
          hideFields={{
            subject: true,
            due_date: true,
            subclient_id: true,
          }}
          name="watchlist-form"
          onSuccess={onFormSuccess}
          onError={onError}
          relationship="takedowns"
          prefillData={{
            subject: 'Watchlist Request',
            description: `I would like to add #${issueId} to a watchlist.`,
            due_date: today.add({ days: 1 }).toString(),
            ticket_id: `${issueId}`,
            to: SUPPORT_TYPES.TAKEDOWN,
          }}
          subjects={['Watchlist Request:']}
          attachment
        />
      </Popover.Dropdown>
    </Popover>
  );
};

export default RequestAddToWatchlist;
